import { useMemo } from 'react';

import { EMPTY_QUOTA, type Quota } from 'domain/quota';
import { useQuota } from 'features/quota/hooks/useQuota';
import { useCompanyQuotaQuery } from 'features/quota/useCompanyQuotaQuery';

import { useAppState } from '../../../AppStateProvider';
import { CompanyTabName, Page } from '../../../domain/page';
import { useEmailsQuotaQuery } from '../../../features/insights/useEmailsQuotaQuery';
import { useIsDataCreditsEnabled } from '../../../features/quota/hooks/useIsDataCreditsEnabled';

const PAGES_TO_SHOW_CREDITS = [Page.Contact, Page.Company, Page.Meeting, Page.Search, Page.Email, Page.GoogleSheets];

interface UseCreditsViewReturn {
  shouldShowCredits: boolean;
  quota: Quota;
  location: CompanyTabName;
}

const useCreditsView = (): UseCreditsViewReturn => {
  const { page, companyTabName } = useAppState();

  const { isDataCreditsEnabled } = useIsDataCreditsEnabled();
  const emailsQuota = useEmailsQuotaQuery()?.data ?? EMPTY_QUOTA;
  const companyQuotaQuery = useCompanyQuotaQuery();
  const companyQuota = companyQuotaQuery.data ?? { ...EMPTY_QUOTA, isUnlimited: false };
  const { quota } = useQuota();

  const getQuota = (): Quota => {
    if (isInsights) {
      return emailsQuota;
    }

    if (isOverview && !isDataCreditsEnabled) {
      return companyQuota;
    }

    return quota;
  };

  const shouldShowCredits = () => {
    if (!isPageIncluded) {
      return false;
    }

    return !(
      isPageIncluded &&
      companyTabName === CompanyTabName.Overview &&
      (!companyQuotaQuery.isSuccess || companyQuota.isUnlimited)
    );
  };

  const isPageIncluded = useMemo(() => PAGES_TO_SHOW_CREDITS.includes(page), [page]);

  const isSearch = page === Page.Search;
  const isInsights = isPageIncluded && !isSearch && companyTabName === CompanyTabName.Insights;
  const isOverview = isPageIncluded && !isSearch && companyTabName === CompanyTabName.Overview;

  return {
    shouldShowCredits: shouldShowCredits(),
    quota: getQuota(),
    location: isInsights ? CompanyTabName.Insights : isOverview ? CompanyTabName.Overview : CompanyTabName.Contacts,
  };
};

export default useCreditsView;
